import { env } from "@config/env";
import { getCSRFToken, setCSRFToken } from "@shared/store/csrfStore";
import { translateApiError } from "@shared/lib/translateApiError";

export interface ApiError {
	message: string;
	status?: number;
	code?: string;
	errors?: Record<string, string[]>;
}

export interface RequestOptions extends RequestInit {
	skipCSRF?: boolean;
	_retried?: boolean;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** True when the endpoint is a fully qualified URL (e.g. presigned upload URL). */
function isAbsoluteUrl(endpoint: string): boolean {
	return /^https?:\/\//i.test(endpoint);
}

class ApiClient {
	private baseURL: string;
	private apiKey: string;
	private refreshPromise: Promise<boolean> | null = null;

	constructor(baseUrl: string) {
		this.baseURL = baseUrl;
		this.apiKey = "";
	}

	setApiKey(key: string) {
		this.apiKey = key;
	}

	private buildUrl(endpoint: string): string {
		return isAbsoluteUrl(endpoint) ? endpoint : `${this.baseURL}${endpoint}`;
	}

	private getEffectiveCSRF(): string | null {
		if (typeof document !== "undefined") {
			const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
			if (match) {
				const cookieVal = decodeURIComponent(match[1]);
				if (cookieVal) return cookieVal;
			}
		}
		return getCSRFToken();
	}

	private async request<T>(
		endpoint: string,
		options: RequestOptions = {},
	): Promise<T> {
		const url = this.buildUrl(endpoint);
		const method = (options.method || "GET").toUpperCase();
		const external = isAbsoluteUrl(endpoint);

		// FormData: dejar que el browser ponga Content-Type con el boundary automáticamente
		const isFormData = options.body instanceof FormData;
		const headers: Record<string, string> = {
			...(isFormData ? {} : { "Content-Type": "application/json" }),
			...(options.headers as Record<string, string>),
		};

		// API Key (optional, for integrations) — never on external presigned URLs.
		if (this.apiKey && !external) {
			headers["X-API-Key"] = this.apiKey;
		}

		// CSRF double-submit on state-changing requests. Skip when the caller
		// targets an external URL (e.g. presigned upload PUT — would invalidate
		// the signature).
		if (
			!options.skipCSRF &&
			!SAFE_METHODS.has(method) &&
			!isAbsoluteUrl(endpoint)
		) {
			const csrf = this.getEffectiveCSRF();
			if (csrf) {
				headers["X-CSRF-Token"] = csrf;
			}
		}

		let response: Response;
		try {
			response = await fetch(url, {
				credentials: external ? "omit" : "include",
				...options,
				method,
				headers,
			});
		} catch {
			throw {
				message: "Error de conexion. Verifica tu conexion a internet.",
				status: 0,
			} as ApiError;
		}

		// Auto-refresh on 401 (one retry) — only for protected endpoints (not /auth/*)
		// and same-origin requests. Absolute URLs (storage) don't carry our cookies anyway.
		if (
			response.status === 401 &&
			!options._retried &&
			!isAbsoluteUrl(endpoint) &&
			!endpoint.startsWith("/auth/")
		) {
			const refreshed = await this.tryRefresh();
			if (refreshed) {
				return this.request<T>(endpoint, { ...options, _retried: true });
			}

			// Si el refresh falló, la sesión expiró realmente.
			setCSRFToken(null);
			if (typeof window !== "undefined") {
				window.dispatchEvent(new CustomEvent("auth:session-expired"));
			}
			throw { message: "Sesion expirada", status: 401 } as ApiError;
		}

		// Auto-refresh on 403 CSRF_FAILED (one retry) — token stale tras F5 o reinicio de backend
		if (
			response.status === 403 &&
			!options._retried &&
			!endpoint.startsWith("/auth/") &&
			!isAbsoluteUrl(endpoint)
		) {
			const cloned = response.clone();
			const errBody = await cloned.json().catch(() => null);
			if (errBody?.code === "CSRF_FAILED") {
				const refreshed = await this.tryRefresh();
				if (refreshed) {
					return this.request<T>(endpoint, { ...options, _retried: true });
				}
				// /auth/refresh también falló: el store en memoria quedó
				// desincronizado de la cookie. No se recarga a ciegas (eso
				// causaba loops/flasheos): se emite el evento y el store
				// limpia la sesión; `ProtectedRoute` redirige a /login.
				if (typeof window !== "undefined") {
					window.dispatchEvent(new CustomEvent("auth:session-expired"));
				}
				throw { message: "Sesion expirada", status: 401 } as ApiError;
			}
		}

		// Capture rotated CSRF token sent by the server on every successful mutation.
		const rotatedCSRF = response.headers.get("X-CSRF-Token");
		if (rotatedCSRF) {
			setCSRFToken(rotatedCSRF);
		}

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({
				message: response.statusText || "Error en la peticion",
			}));
			const raw: ApiError = {
				message: errorData.error || errorData.message || "Error desconocido",
				status: response.status,
				code: errorData.code,
				errors: errorData.errors,
			};
			// Translate to Spanish at the throw site so every caller — even
			// ones that don't go through handleApiError — gets a localized
			// message in `err.message`. The structured fields (status, code,
			// errors) are kept so handlers can still branch on them.
			raw.message = translateApiError(raw).message;
			throw raw;
		}

		if (response.status === 204) {
			return {} as T;
		}

		const json = await response.json().catch(() => null);
		if (json === null) return {} as T;

		return json && typeof json === "object" && "data" in json
			? json.data
			: json;
	}

	/**
	 * Calls /auth/refresh, deduplicating concurrent attempts so a burst of
	 * parallel 401s only triggers one refresh round-trip.
	 */
	private tryRefresh(): Promise<boolean> {
		if (this.refreshPromise) return this.refreshPromise;
		this.refreshPromise = (async () => {
			try {
				const res = await fetch(`${this.baseURL}/auth/refresh`, {
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
				});
				if (!res.ok) return false;
				const json = await res.json();
				const data = json?.data ?? json;
				if (data?.csrf_token) {
					setCSRFToken(data.csrf_token);
				}
				return true;
			} catch {
				return false;
			} finally {
				this.refreshPromise = null;
			}
		})();
		return this.refreshPromise;
	}

	get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
		return this.request<T>(endpoint, { ...options, method: "GET" });
	}

	/**
	 * POST. `data` may be:
	 *   - a plain object (JSON-stringified),
	 *   - a `FormData` instance (multipart, browser sets the boundary),
	 *   - a `Blob`/`ArrayBuffer` (binary upload — e.g. presigned PUT body).
	 */
	post<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<T> {
		const body = this.serializeBody(data);
		return this.request<T>(endpoint, { ...options, method: "POST", body });
	}

	put<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<T> {
		const body = this.serializeBody(data);
		return this.request<T>(endpoint, {
			...options,
			method: "PUT",
			body,
		});
	}

	patch<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<T> {
		return this.request<T>(endpoint, {
			...options,
			method: "PATCH",
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	delete<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<T> {
		const body = this.serializeBody(data);
		return this.request<T>(endpoint, { ...options, method: "DELETE", body });
	}

	postForm<T>(
		endpoint: string,
		form: FormData,
		options?: RequestOptions,
	): Promise<T> {
		// Do NOT set Content-Type — browser sets it with correct boundary for multipart
		return this.request<T>(endpoint, {
			...options,
			method: "POST",
			body: form,
		});
	}

	async getBlob(endpoint: string): Promise<Blob> {
		const url = this.buildUrl(endpoint);
		const headers: Record<string, string> = {};
		if (this.apiKey) headers["X-API-Key"] = this.apiKey;
		const response = await fetch(url, { credentials: "include", headers });
		if (!response.ok)
			throw {
				message: response.statusText || "Error al descargar",
				status: response.status,
			};
		return response.blob();
	}

	private serializeBody(
		data: unknown,
		opts: { jsonify?: boolean } = {},
	): BodyInit | undefined {
		if (data === undefined || data === null) return undefined;
		if (data instanceof FormData) return data;
		if (
			data instanceof Blob ||
			data instanceof ArrayBuffer ||
			// biome-ignore lint/suspicious/noExplicitAny: ArrayBufferLike covers SharedArrayBuffer
			(data as any) instanceof ArrayBuffer
		) {
			return data as BodyInit;
		}
		const jsonify = opts.jsonify ?? true;
		return jsonify ? JSON.stringify(data) : (data as BodyInit);
	}
}

export const apiClient = new ApiClient(env.API_URL);

/**
 * Apply env-driven configuration once at boot. Called from `main.tsx`
 * before mounting React. Centralizes env access so feature code never
 * reads `import.meta.env.*` directly.
 */
export function configureApiClient() {
	apiClient.setApiKey(env.API_KEY);
}
